import { formatNum, formatPercentArray, formatUsd, formatTokenAmount, bsMath } from "@bridgesplit/utils";
import { Row, Table, TableColumn, Text, createRows, equalWidthColumns, Column } from "@bridgesplit/ui";
import { OracleQuote, TokenListMetadata } from "@bridgesplit/abf-sdk";
import {
    BsMetaUtil,
    isWhirlpoolMetadata,
    RoleView,
    useStatsQuery,
    UnifiedStatsResponse,
    AssetTypeIdentifier
} from "@bridgesplit/abf-react";
import { COPY } from "app/constants";

import { CollateralMarket } from "./types";
import { TokenImage, formatLoopPtTokenName, getMarketPath } from "../../common";
import { RewardsCollateralLabel } from "../../points";

export default function CollateralMarketsTable({
    markets,
    marketsLoading
}: {
    markets: CollateralMarket[] | undefined;
    marketsLoading: boolean;
}) {
    const { data: stats, isLoading: statsLoading } = useStatsQuery({ includePrincipalToCollateralDepositedUsd: true });

    const columns = getCollateralMarketsTableColumns(stats);
    return (
        <Table
            onRowNavigatePath={({ data: { metadata, principalMetadata } }) =>
                getMarketPath({
                    view: RoleView.Borrow,
                    token: principalMetadata,
                    collateralMint: metadata.mint
                })
            }
            paginationProps={{
                pageSize: 5,
                type: "managed",
                defaultSortKey: "balance"
            }}
            emptyPlaceholder="No collateral found"
            loading={marketsLoading || statsLoading}
            rows={createRows(markets, (s) => s.metadata.mint)}
            columns={equalWidthColumns(columns)}
        />
    );
}

function getCollateralMarketsTableColumns(stats: UnifiedStatsResponse | undefined) {
    const columns: TableColumn<CollateralMarket>[] = [
        {
            dataIndex: "asset",
            title: "Asset",
            render: ({ data }) => (
                <Row spacing={1}>
                    <TokenImage metadata={data.metadata} size="md" />
                    <Text>{BsMetaUtil.getSymbolUnique(data.metadata)}</Text>
                    <RewardsCollateralLabel metadata={data.metadata} />
                </Row>
            ),
            sortBy: ({ data: { metadata } }) => BsMetaUtil.getSymbol(metadata)
        },
        {
            dataIndex: "balance",
            title: "Your balance",
            align: "right",
            render: ({ data: { metadata, userBalance } }) => {
                if (!userBalance) {
                    return <Text color="disabled"> 0 </Text>;
                }
                if (BsMetaUtil.isNonFungible(metadata) && isWhirlpoolMetadata(metadata)) {
                    return (
                        <>
                            {formatNum(userBalance)} {BsMetaUtil.getSymbol(metadata)}
                        </>
                    );
                }
                return <> {BsMetaUtil.formatAmount(metadata, userBalance)} </>;
            },
            sortBy: ({ data: { usdValue } }) => usdValue
        },
        {
            dataIndex: "ltv",
            title: "LTV",
            align: "right",
            render: ({ data: { markets } }) => <> {formatPercentArray(markets.map((m) => m.ltv))} </>,
            sortBy: ({ data: { markets } }) => markets.reduce((acc, curr) => Math.max(acc, curr.ltv), 0)
        },
        {
            dataIndex: "liquidationThreshold",
            toolTip: COPY.LIQUIDATION_THRESHOLD_TOOLTIP,
            title: COPY.LIQUIDATION_THRESHOLD_TERM,
            align: "right",
            render: ({ data: { markets } }) => <> {formatPercentArray(markets.map((m) => m.liquidationThreshold))} </>,
            sortBy: ({ data: { markets } }) =>
                markets.reduce((acc, curr) => Math.max(acc, curr.liquidationThreshold), 0)
        },
        {
            dataIndex: "collateralDepositedUsd",
            title: COPY.COLLATERAL_DEPOSITS_TERM,
            align: "right",
            render: ({ data: { principalMetadata, metadata, oracle } }) => {
                return (
                    <CollateralDepositedStat
                        stats={stats}
                        principalMetadata={principalMetadata}
                        collateralMetadata={metadata}
                        collateralOracle={oracle}
                    />
                );
            },
            sortBy: ({ data: { principalMetadata, metadata } }) =>
                getUsdDepositsForPrincipalCollateralPair(stats, principalMetadata, metadata) ?? 0
        }
    ];

    return columns;
}

function CollateralDepositedStat({
    stats,
    principalMetadata,
    collateralMetadata,
    collateralOracle
}: {
    stats: UnifiedStatsResponse | undefined;
    principalMetadata: TokenListMetadata | undefined;
    collateralMetadata: TokenListMetadata | undefined;
    collateralOracle: OracleQuote | null;
}) {
    const usdDeposits = getUsdDepositsForPrincipalCollateralPair(stats, principalMetadata, collateralMetadata);
    const isOrcaMetadata = collateralMetadata?.assetType === AssetTypeIdentifier.OrcaPosition;

    if (usdDeposits === 0) return <Text color="disabled">0</Text>;
    if (isOrcaMetadata) {
        return <Text>{formatUsd(usdDeposits)}</Text>;
    }

    const collateralAmount = bsMath.div(usdDeposits, collateralOracle?.usdPrice);

    return (
        <Column alignItems="flex-end">
            <Text>
                {formatTokenAmount(collateralAmount, {
                    decimals: collateralMetadata?.decimals,
                    symbol: formatLoopPtTokenName(collateralMetadata)
                })}
            </Text>
            <Text color="caption" variant="body2">
                {formatUsd(usdDeposits)}
            </Text>
        </Column>
    );
}

function getUsdDepositsForPrincipalCollateralPair(
    stats: UnifiedStatsResponse | undefined,
    principalMetadata: TokenListMetadata | undefined,
    collateralMetadata: TokenListMetadata | undefined
) {
    const principalMint = principalMetadata?.mint;
    const collateralMint = collateralMetadata?.mint;
    if (!principalMint || !collateralMint) {
        return undefined;
    }
    return stats?.principalToDepositedCollateralUsd?.[principalMint]?.[collateralMint] ?? 0;
}
